import { ValidationErrorDetail, ValidationStatusEntity } from 'src/models/XptGenericModels';
import { ValidationService } from './ValidationService';
import ForecastValidationMessages from '../forecast-utils/ValidationMessages';
import { ForecastGridFixedFields } from '../forecast-utils/ForecastGridConstants';
import { buildSystemRowLookup } from '../forecast-utils/RowResetUtils';
import { logger } from 'src/analytics/KatalLogger';
import { AccountBudgetTypeMapping } from 'src/models/xPTMappingModels';
import { CorpSegmentNames } from 'src/constants/corp-segment-constants';

/**
 * Service for validating budget type and account combinations in forecast templates
 */
export class BudgetTypeValidationService extends ValidationService {
  /**
   * Validates that the budget type and account combinations are valid.
   * @param forecastTemplateRelevantRowData The array of relevant rows to validate.
   * @param accountBudgetTypeMapping The mapping of accounts to budget types.
   * @param forecastTemplateCompleteData Optional system-side row snapshot. When supplied,
   *   BASELINE rows (row has a Line Item Id and a matching system row exists) whose Account AND
   *   Budget Type are UNCHANGED from the system version are skipped. Account is locked on
   *   baseline rows (see NonEditableFieldsValidationService), so re-checking against the current
   *   `accountBudgetTypeMapping` here would surface "not in the mapping" errors on rows the
   *   user can't fix — a dead end when the master data drifted since the row was submitted
   *   (account retired, budget-type re-tagged, etc.). Carry those over; still validate NEW
   *   rows and any row where the user actually edited Account or Budget Type.
   * @returns Promise with validation status
   */
  public async validateBudgetTypeAndAccountCombination(
    forecastTemplateRelevantRowData: any[],
    accountBudgetTypeMapping: AccountBudgetTypeMapping[],
    forecastTemplateCompleteData?: any[]
  ): Promise<ValidationStatusEntity> {
    try {
      const validationErrorDetails: ValidationErrorDetail[] = [];

      logger.info(`Validating budget type and account combination for ${forecastTemplateRelevantRowData.length} records`);

      const systemRowByLineItemId = forecastTemplateCompleteData ? buildSystemRowLookup(forecastTemplateCompleteData) : undefined;

      forecastTemplateRelevantRowData.forEach((row, rowIndex) => {
        const rowNo = (row[ForecastGridFixedFields.FileRowNumber.value] as number) ?? rowIndex + 1;
        const accountCode = row[CorpSegmentNames.ACCOUNT];
        const budgetType = row[ForecastGridFixedFields.BudgetType.value];

        // Carry over baseline rows whose auth-relevant fields are unchanged. If the user only
        // edited forecast months on a row whose Account was retired from the mapping since it
        // was originally submitted, the historical value is authoritative and the user can't
        // fix Account anyway (locked). Skip to avoid a dead-end error.
        if (systemRowByLineItemId && !row[ForecastGridFixedFields.IsNew.value]) {
          const lineItemId = row[ForecastGridFixedFields.XptLineItemId.value];
          const systemRow = lineItemId != null ? systemRowByLineItemId.get(`${lineItemId}`) : undefined;
          if (systemRow) {
            const accountUnchanged = `${systemRow[CorpSegmentNames.ACCOUNT]}` === `${accountCode}`;
            const budgetTypeUnchanged = `${systemRow[ForecastGridFixedFields.BudgetType.value]}` === `${budgetType}`;
            if (accountUnchanged && budgetTypeUnchanged) return;
          }
        }

        const accountBudgetTypeEntry = accountBudgetTypeMapping.find((entry) => entry.account_code === accountCode);

        if (!accountBudgetTypeEntry) {
          validationErrorDetails.push({
            rowIndex: rowNo,
            field: CorpSegmentNames.ACCOUNT,
            message: `Account ${accountCode} not found in the mapping ${this.getLineItemIdMessagePart(
              row,
              ForecastGridFixedFields.XptLineItemId.value
            )}`
          });
        }

        if (accountBudgetTypeEntry && accountBudgetTypeEntry.budget_type !== budgetType) {
          validationErrorDetails.push({
            rowIndex: rowNo,
            field: ForecastGridFixedFields.BudgetType.value,
            message: `Budget Type ${budgetType} is not valid for Account ${accountCode} ${this.getLineItemIdMessagePart(
              row,
              ForecastGridFixedFields.XptLineItemId.value
            )}`
          });
        }
      });

      // Log validation errors if any
      this.logValidationErrors('Budget Type & Account Mapping', validationErrorDetails);

      // Return appropriate validation status
      if (validationErrorDetails.length > 0) {
        return this.createErrorStatus(
          ForecastValidationMessages.ACCOUNT_BUDGET_TYPE_MAPPING_VALIDATION_FAILED,
          ForecastValidationMessages.ACCOUNT_BUDGET_TYPE_MAPPING_VALIDATION_DEFAULT,
          validationErrorDetails
        );
      }

      return this.createSuccessStatus(
        ForecastValidationMessages.ACCOUNT_BUDGET_TYPE_MAPPING_VALIDATION_SUCCESS,
        ForecastValidationMessages.ACCOUNT_BUDGET_TYPE_MAPPING_VALIDATION_DEFAULT
      );
    } catch (error: any) {
      logger.error('Error validating Budget Type & Account Mapping:', error);
      return this.createErrorStatus(
        ForecastValidationMessages.ACCOUNT_BUDGET_TYPE_MAPPING_VALIDATION_ERROR,
        ForecastValidationMessages.ACCOUNT_BUDGET_TYPE_MAPPING_VALIDATION_DEFAULT,
        [{ message: error.message || 'Unknown error validating budget type and account combination' }]
      );
    }
  }
}

// Export singleton instance
export const budgetTypeValidationService = new BudgetTypeValidationService();
